// components/hauler/CompletionModal.js
// Confirmation step for job completion.
//
// Shows a verification summary + a recomputed payout breakdown based
// on the verified tier, then submits via driverJobService.completeJob.
//
// Bucket A+B contract: estimate framing only. Snapshot-lock copy is
// out of scope per commit a189e9d's discipline statement. The data
// layer still persists payout fields (driver_pay_amt etc) at
// completion via supabase.completeJob.
//
// LEU numerics are never rendered. The provider-verified LEU is
// derived internally from the verified tier key for the API call.

import React, { useState } from "react";
import { LOAD_TIERS } from "../../constants";
import { calcSplit, buildCostSnapshot, getTierPrice } from "../../utils/marketConfig";
import { completeJob as serviceCompleteJob } from "../../services/driverJobService";
import { uploadPhoto } from "../../services/supabaseService";
import { isJobAllowedForTruck } from "../../dispatch/truckCapacityPolicy";
import { logEligibilityOutcome } from "../../eligibility/eligibilityLogger";
import { EVENTS, trackRoleContextEvent } from "../../analytics/roleContextTelemetry";
import { VERIFIED_TIER_LABEL } from "./VerificationBlock";

const DEFAULT_MARKET = "ut-hill-afb";

const COLORS = {
  scrim: "rgba(10,22,40,.85)",
  card: "#FFFFFF",
  text: "#0A1628",
  sub: "#5e6772",
  muted: "#9ca3af",
  border: "#E5E7EB",
  green: "#03A66A",
  sky: "#3D90D9",
  red: "#D95252",
};

function fmt$(n) { if (n == null || isNaN(n)) return "$0.00"; return "$" + Number(n).toFixed(2); }
function leuFor(tierKey) { return LOAD_TIERS.find(t => t.key === tierKey)?.fill ?? null; }

// Flag labels match the canonical strings exactly so the retired /drivers
// route and canonical /haulers route feed identical values into driverJobService.completeJob.
// IC-safe copy: descriptive, never punitive.
const ISSUE_FLAGS = [
  { key: "Load misrepresented", desc: "The load was bigger or different from what was booked." },
  { key: "Not at staging",      desc: "The load wasn't at the agreed staging spot when you arrived." },
  { key: "Access blocked",      desc: "Something blocked access (locked gate, parked vehicle, etc.)." },
  { key: "Hazardous",           desc: "Items not allowed by service terms (chemicals, electronics, etc.)." },
];

export default function CompletionModal({
  job,
  session,
  verifiedTier,
  staging,
  photoFile,
  bagCount = "",
  isBagJob = false,
  vehicleProfile = null,
  addons = [],
  onCancel,
  onCompleted,
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [selectedFlags, setSelectedFlags] = useState([]);

  function toggleFlag(key) {
    setSelectedFlags(prev =>
      prev.includes(key) ? prev.filter(f => f !== key) : [...prev, key]
    );
  }

  const userId = session?.user?.id || null;
  const marketId = job?.market_id || DEFAULT_MARKET;
  const zoneKey = job?.zone || null;

  // Payout is computed on the VERIFIED tier — the Hauler is paid for the load
  // actually hauled, not the tier the customer originally booked (owner ruling
  // 2026-07-04; matches safety invariant 6, and matches the /drivers path which
  // already pays on the verified tier). The verified gross is what the customer
  // effectively owes once the misrepresentation fee reconciles any difference
  // from the booked tier. The booked tier is still recorded separately below
  // (systemAssignedTier / systemAssignedLeu / predictedLeuSnapshot).
  const bookedTier = job?.size;
  let verifiedPrice = 0;
  try {
    verifiedPrice = getTierPrice(marketId, verifiedTier) || 0;
  } catch {
    verifiedPrice = 0;
  }
  let split;
  try {
    split = calcSplit(verifiedPrice, marketId, zoneKey, { quick: job?.quick === true, mattressPieces: job?.mattress_count });
  } catch {
    split = { gross: 0, stripeFee: 0, fuelEst: 0, dumpFee: 0, companyMargin: 0, driverPay: 0 };
  }

  const verifiedLeu = leuFor(verifiedTier);
  const bookedLeu = leuFor(bookedTier);

  async function submit() {
    if (busy) return;
    if (!userId) { setError("Sign in required."); return; }
    if (!verifiedTier) { setError("Pick a verified tier."); return; }
    if (!staging || !staging.trim()) { setError("Add a staging note."); return; }
    if (!photoFile) { setError("Capture a completion photo."); return; }

    // Capacity re-check (Gap 3) — block completion if the verified
    // tier exceeds the Hauler's truck capacity. Mirrors DriverScreen
    // line 530's safety backstop. Friendly message points the Hauler
    // at the misrepresentation flag instead of a hard reject.
    if (vehicleProfile) {
      const eligibility = isJobAllowedForTruck(vehicleProfile, { ...job, size: verifiedTier });
      if (!eligibility?.allowed) {
        setError("Verified tier exceeds your truck capacity. Flag the load as misrepresented above instead.");
        return;
      }
    }

    setBusy(true);
    setError(null);
    try {
      const photoUrl = await uploadPhoto(photoFile, userId);
      if (!photoUrl) {
        throw new Error("Photo upload failed.");
      }
      trackRoleContextEvent(EVENTS.DRIVER_DOCS_UPLOADED, userId, {
        intent: "hauler_interest",
        role_context: "hauler",
        surface: "hauler_app",
        page: "job_completion",
        request_id: job.id,
        document_type: "completion_photo",
      });

      const variableCostAmt = Math.round((split.stripeFee + split.dumpFee + split.fuelEst) * 100) / 100;
      const zoneCostSnapshot = buildCostSnapshot(marketId, zoneKey, { mattressPieces: job?.mattress_count });

      // Bag count append (Gap 4) — mirrors DriverScreen line 550.
      const trimmedBagCount = String(bagCount).trim();
      const submittedFlags = isBagJob && trimmedBagCount
        ? [...selectedFlags, `bag_count:${trimmedBagCount}`]
        : selectedFlags;

      await serviceCompleteJob({
        requestId: job.id,
        driverUserId: userId,
        providerVerifiedLeu: verifiedLeu,
        confirmedStaging: staging.trim(),
        flags: submittedFlags,
        systemAssignedLeu: bookedLeu,
        systemAssignedTier: bookedTier,
        driverVerifiedTier: verifiedTier,
        completionPhotoUrl: photoUrl,
        predictedLeuSnapshot: bookedLeu,
        companyMarginAmt: split.companyMargin,
        driverPayAmt: split.driverPay,
        variableCostAmt,
        marketId,
        zoneCostSnapshot,
      });

      // Eligibility outcome audit (Gap 6) — non-fatal. trailer_required
      // and trailer_used left null pending Gap 2 direction (per-stop
      // trailer toggle vs profile-only). When Gap 2 lands, fill these.
      logEligibilityOutcome({
        driver_id: userId,
        job_id: job.id,
        trailer_required: null,
        trailer_used: null,
        success: true,
      }).catch(() => {});

      onCompleted && onCompleted();
    } catch (e) {
      // Validation messages set by submit() above are already user-friendly
      // ('Sign in required', 'Pick a verified tier', etc.). Anything thrown
      // from the service / upload paths is technical — keep that in console
      // and show a generic message to the user.
      console.error("[CompletionModal] completion failed:", e);
      setError("Couldn't submit completion. Check your connection and try again.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      style={{
        position: "fixed", inset: 0, zIndex: 200,
        background: COLORS.scrim,
        display: "flex", alignItems: "flex-end", justifyContent: "center",
      }}
      onClick={(e) => { if (e.target === e.currentTarget && !busy) onCancel && onCancel(); }}
    >
      <div style={{
        background: COLORS.card,
        color: COLORS.text,
        width: "100%",
        boxSizing: "border-box",
        maxWidth: 520,
        borderRadius: "20px 20px 0 0",
        maxHeight: "92vh",
        overflowY: "auto",
        padding: "20px 18px calc(20px + env(safe-area-inset-bottom, 0px))",
      }}>
        <div style={{ fontWeight: 800, fontSize: 18, marginBottom: 4 }}>
          Confirm completion
        </div>
        <div style={{ fontSize: 12, color: COLORS.sub, marginBottom: 16 }}>
          Verify the details before submitting.
        </div>

        {/* Verification summary */}
        <Group title="Verification">
          <Row label="Verified tier" value={VERIFIED_TIER_LABEL[verifiedTier] || "—"} />
          <Row label="Staging" value={staging || "—"} />
          <Row label="Photo" value={photoFile ? "Captured" : "Missing"} />
        </Group>

        {/* Declared add-ons — the exception to misrepresentation. Items the
            customer declared through the add-on flow are part of the job:
            they belong in the verified size and must not trigger the
            "Load misrepresented" flag on their own (owner ruling 2026-07-11).
            Rendered directly above the flags so the contrast sits next to
            the flag the Hauler would otherwise reach for. */}
        {addons.length > 0 && (
          <div style={{
            background: "rgba(61,144,217,.08)",
            border: "1px solid rgba(61,144,217,.35)",
            borderRadius: 10,
            padding: "10px 12px",
            marginBottom: 14,
          }}>
            <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: 1.2, textTransform: "uppercase", color: COLORS.sky, marginBottom: 6 }}>
              Added after booking · approved
            </div>
            {addons.map((a, i) => (
              <div key={i} style={{ fontSize: 12.5, lineHeight: 1.5, color: COLORS.text }}>
                • {a.description}{a.added_mattress_pieces > 0 ? ` (+${a.added_mattress_pieces} mattress piece${a.added_mattress_pieces > 1 ? "s" : ""})` : ""}
              </div>
            ))}
            <div style={{ fontSize: 11, color: COLORS.sub, marginTop: 6, lineHeight: 1.45 }}>
              The customer declared these through LoadDash after booking. Count
              them in your verified size. Added items are not misrepresentation;
              flag that only if the load goes beyond the booking plus this list.
            </div>
          </div>
        )}

        {/* Optional issue flags — descriptive, not punitive. */}
        <Group title="Report an issue (optional)">
          <div style={{ fontSize: 12, color: COLORS.sub, marginBottom: 8, lineHeight: 1.5 }}>
            Tell us what happened so we can adjust. Nothing here counts against the customer or against you.
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {ISSUE_FLAGS.map(opt => {
              const active = selectedFlags.includes(opt.key);
              return (
                <button
                  key={opt.key}
                  type="button"
                  onClick={() => toggleFlag(opt.key)}
                  style={{
                    border: `1px solid ${active ? "#03A66A" : COLORS.border}`,
                    borderRadius: 10,
                    background: active ? "rgba(3,166,106,.08)" : "#FFFFFF",
                    padding: "10px 12px",
                    fontFamily: "inherit",
                    textAlign: "left",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 10,
                    minHeight: 44,
                  }}
                >
                  <span style={{
                    flexShrink: 0,
                    width: 18, height: 18, borderRadius: 5,
                    border: `1.5px solid ${active ? "#03A66A" : COLORS.border}`,
                    background: active ? "#03A66A" : "transparent",
                    display: "inline-flex", alignItems: "center", justifyContent: "center",
                    color: "#fff", fontSize: 12, fontWeight: 800,
                    marginTop: 1,
                  }}>
                    {active ? "✓" : ""}
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.text }}>{opt.key}</div>
                    <div style={{ fontSize: 11, color: COLORS.sub, marginTop: 2, lineHeight: 1.4 }}>{opt.desc}</div>
                  </div>
                </button>
              );
            })}
          </div>
        </Group>

        {/* Payout estimate (no snapshot-lock framing per Bucket A+B) */}
        <Group title="Estimated payout">
          <Row label="Customer pays" value={fmt$(split.gross)} />
          <Row label="LoadDash service fee (10%)" value={"−" + fmt$(split.companyMargin)} />
          {/* Variable costs are paid by the Hauler, not retained by LoadDash. */}
          <div style={{ fontSize: 11, color: COLORS.muted, marginTop: 4, fontWeight: 700, letterSpacing: 0.4, textTransform: "uppercase" }}>
            Hauler's potential expenses
          </div>
          <Row label="Payment processing estimate (3.5%)" value={"−" + fmt$(split.stripeFee)} />
          <Row label="Disposal allowance" value={"−" + fmt$(split.dumpFee)} />
          {/* Mattress gate fee is a real out-of-pocket at the scale, but it is
              reimbursed inside the transfer (customer's $15/piece surcharge
              passes through; company absorbs the card fee on it). Itemize
              BOTH sides so the payout doesn't look $15/piece short at the
              gate — first real haul 2026-07-11 read exactly that way. */}
          {(split.mattressReimbursement || 0) > 0 && (
            <Row label="Mattress gate fee (reimbursed below)" value={"−" + fmt$(split.mattressReimbursement)} />
          )}
          <Row label="Fuel allowance" value={"−" + fmt$(split.fuelEst)} />
          {(split.mattressReimbursement || 0) > 0 && (
            <Row label="Mattress reimbursement, in your pay" value={"+" + fmt$(split.mattressReimbursement)} />
          )}
          <div style={{ height: 1, background: COLORS.border, margin: "6px 0" }} />
          <Row label="Estimated Hauler payout" value={fmt$(split.driverPay)} bold />
          {(split.mattressReimbursement || 0) > 0 && (
            <div style={{ fontSize: 11, color: COLORS.sub, marginTop: 6, lineHeight: 1.45 }}>
              Pay the mattress fee at the gate. It is already added back into
              the payout above, on top of the base pay for this size.
            </div>
          )}
        </Group>

        {error && (
          <div style={{
            background: "#FEF2F2",
            border: `1px solid ${COLORS.red}`,
            color: COLORS.red,
            borderRadius: 10,
            padding: "10px 12px",
            fontSize: 12,
            fontWeight: 700,
            marginBottom: 10,
          }}>
            {error}
          </div>
        )}

        <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
          <button
            disabled={busy}
            onClick={onCancel}
            style={{
              flex: 1,
              padding: "13px",
              borderRadius: 12,
              border: `1px solid ${COLORS.border}`,
              background: "#FFFFFF",
              color: COLORS.sub,
              fontFamily: "inherit",
              fontSize: 14,
              fontWeight: 700,
              cursor: busy ? "wait" : "pointer",
              minHeight: 48,
            }}
          >
            Cancel
          </button>
          <button
            disabled={busy}
            onClick={submit}
            style={{
              flex: 2,
              padding: "13px",
              borderRadius: 12,
              border: "none",
              background: busy ? "rgba(3,166,106,.5)" : COLORS.green,
              color: "#FFFFFF",
              fontFamily: "inherit",
              fontSize: 14,
              fontWeight: 800,
              cursor: busy ? "wait" : "pointer",
              minHeight: 48,
            }}
          >
            {busy ? "Submitting…" : "Confirm completion"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Group({ title, children }) {
  return (
    <div style={{
      border: `1px solid ${COLORS.border}`,
      borderRadius: 12,
      padding: "12px",
      marginBottom: 12,
    }}>
      <div style={{ fontSize: 11, letterSpacing: 1.5, fontWeight: 800, color: COLORS.muted, textTransform: "uppercase", marginBottom: 8 }}>
        {title}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {children}
      </div>
    </div>
  );
}

function Row({ label, value, bold }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}>
      <span style={{ color: "#5e6772" }}>{label}</span>
      <span style={{ color: bold ? COLORS.green : COLORS.text, fontWeight: bold ? 800 : 500 }}>{value}</span>
    </div>
  );
}
